import { LoadStatus } from '../load-status';

/**
 * Feature key under which the date-manipulation-check slice is registered in the root store.
 */
export const DATE_MANIPULATION_CHECK_FEATURE_KEY = 'dateManipulationCheck';

/**
 * Allowed deviation between a candidate timestamp and the last valid internal/external
 * timestamp, ported from the legacy `moment.duration(1, 'minutes')`. Covers ordinary clock drift
 * and request latency without weakening the manipulation check.
 */
export const TIMESTAMP_CHECK_TOLERANCE_MS = 60_000;

/**
 * Records what the last {@link dateManipulationCheckTimestampReducer} run decided and on what
 * basis, so the reporting effects (`reportCheck$`, `appLock$`) can log and lock without
 * recomputing anything.
 */
export type TimestampCheckResult = Readonly<{
    checkedTimestamp: string;
    previousInternalTimestamp: string;
    previousExternalTimestamp: string;
    valid: boolean;
}>;

/**
 * The two timestamps that survive a restart. `strictStateSerializability` is on, so both are ISO
 * strings; the empty string is the serializable stand-in for `moment.invalid()` — never `null`,
 * never an explicitly-assigned `undefined`.
 */
export type PersistedTimestamps = Readonly<{
    lastValidInternalTimestamp: string;
    lastValidExternalTimestamp: string;
}>;

export type DateManipulationCheckState = Readonly<
    PersistedTimestamps & {
        initialized: boolean;
        loadStatus: LoadStatus;
        lastCheck: TimestampCheckResult;
    }
>;

/**
 * "Nothing checked yet" — every field empty/invalid/false, matching the "not set" sentinel used
 * for the timestamps themselves.
 */
export function createDefaultTimestampCheckResult(): TimestampCheckResult {
    return {
        checkedTimestamp: '',
        previousInternalTimestamp: '',
        previousExternalTimestamp: '',
        valid: false,
    };
}

export const initialDateManipulationCheckState: DateManipulationCheckState = {
    lastValidInternalTimestamp: '',
    lastValidExternalTimestamp: '',
    initialized: false,
    loadStatus: LoadStatus.Idle,
    lastCheck: createDefaultTimestampCheckResult(),
};
